import { useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LabelList,
} from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import {
  DashFrame, KpiCard, FilterSelect, DataTable, ChartCaption, ChartTitle, LegendDots, StatusPill,
  usePalette, GRID, AXIS, TOOLTIP_STYLE, CURSOR,
} from "./kit";

const ON_TIME_TARGET = 95; // % — favourable performance threshold
import { euro } from "../lib/format";

const initial = { year: "All years", supplier: "All suppliers", segment: "All segments" };

export default function SupplyChainDash({ rows }) {
  const [f, setF] = useState(initial);
  const c = usePalette();
  const years = [...new Set(rows.map((r) => r.month.slice(0, 4)))];
  const suppliers = [...new Set(rows.map((r) => r.supplier))];
  const segments = [...new Set(rows.map((r) => r.segment))];

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (f.year === "All years" || r.month.startsWith(f.year)) &&
          (f.supplier === "All suppliers" || r.supplier === f.supplier) &&
          (f.segment === "All segments" || r.segment === f.segment)
      ),
    [rows, f]
  );

  const kpis = useMemo(() => {
    const orders = filtered.reduce((s, r) => s + r.orders, 0);
    const late = filtered.reduce((s, r) => s + r.late, 0);
    const sales = filtered.reduce((s, r) => s + r.sales, 0);
    const marginW = filtered.reduce((s, r) => s + r.sales * r.margin, 0);
    return {
      orders, late, sales,
      onTime: orders ? ((orders - late) / orders) * 100 : 0,
      margin: sales ? (marginW / sales) * 100 : 0,
    };
  }, [filtered]);

  const trend = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      m[r.month] = m[r.month] || { sales: 0, marginW: 0 };
      m[r.month].sales += r.sales;
      m[r.month].marginW += r.sales * r.margin;
    });
    return Object.entries(m)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, v]) => ({
        month: month.slice(2),
        sales: Math.round(v.sales),
        margin: Number(((v.marginW / Math.max(v.sales, 1)) * 100).toFixed(1)),
      }));
  }, [filtered]);

  const bySupplier = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      m[r.supplier] = m[r.supplier] || { supplier: r.supplier.replace(/ (Logistics|Packaging|Timber|Ceramics|Glass|Metals)$/, ""), orders: 0, late: 0, sales: 0, marginW: 0 };
      const s = m[r.supplier];
      s.orders += r.orders; s.late += r.late; s.sales += r.sales; s.marginW += r.sales * r.margin;
    });
    return Object.values(m)
      .map((s) => ({
        ...s,
        short: s.supplier,
        onTime: Number((((s.orders - s.late) / Math.max(s.orders, 1)) * 100).toFixed(1)),
        marginPct: Number(((s.marginW / Math.max(s.sales, 1)) * 100).toFixed(1)),
      }))
      .sort((a, b) => b.late - a.late);
  }, [filtered]);

  return (
    <DashFrame
      title="Supply chain & sales performance"
      testid="dash-supply-chain"
      filters={
        <>
          <FilterSelect label="Period" testid="filter-sc-period" value={f.year}
            options={["All years", ...years]} onChange={(v) => setF({ ...f, year: v })} />
          <FilterSelect label="Supplier" testid="filter-sc-supplier" value={f.supplier}
            options={["All suppliers", ...suppliers]} onChange={(v) => setF({ ...f, supplier: v })} />
          <FilterSelect label="Business segment" testid="filter-sc-segment" value={f.segment}
            options={["All segments", ...segments]} onChange={(v) => setF({ ...f, segment: v })} />
        </>
      }
      onReset={() => setF(initial)}
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiCard label="Orders" value={euro(kpis.orders).replace("€", "")} sub="Sample orders" accent={c.navy} />
        <KpiCard label="Late deliveries" value={euro(kpis.late).replace("€", "")} accent={c.orange} />
        <KpiCard label="On-time delivery" value={`${kpis.onTime.toFixed(1)}%`} sub={kpis.onTime >= ON_TIME_TARGET ? `At or above ${ON_TIME_TARGET}% target` : `Below ${ON_TIME_TARGET}% target`} accent={kpis.onTime >= ON_TIME_TARGET ? c.teal : c.orange} />
        <KpiCard label="Sales" value={euro(kpis.sales)} sub="Sample revenue" accent={c.navy} />
        <KpiCard label="Margin" value={`${kpis.margin.toFixed(1)}%`} sub="Revenue-weighted" accent={c.blue} />
      </div>

      <div className="mt-8">
        <ChartTitle color={c.navy}>Sales and margin trend by month</ChartTitle>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="month" tick={{ ...AXIS, fontSize: 10 }} />
              <YAxis yAxisId="l" tick={AXIS} tickFormatter={(v) => `€${Math.round(v / 1000)}k`} />
              <YAxis yAxisId="r" orientation="right" tick={AXIS} tickFormatter={(v) => `${v}%`} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="l" type="monotone" dataKey="sales" name="Sales (€)" stroke={c.navy} strokeWidth="2.5" dot={false} />
              <Line yAxisId="r" type="monotone" dataKey="margin" name="Margin % (dashed)" stroke={c.orange} strokeWidth="2" strokeDasharray="5 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <ChartTitle color={c.navy}>Late orders by supplier</ChartTitle>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bySupplier} layout="vertical" margin={{ top: 4, right: 16, left: 20, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis type="number" tick={{ ...AXIS, fontSize: 10 }} />
                <YAxis type="category" dataKey="short" width={90} tick={AXIS} />
                <Tooltip formatter={(v) => `${v} late orders`} cursor={CURSOR} contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="late" name="Late orders" radius={[0, 3, 3, 0]}>
                  {bySupplier.map((r, i) => (
                    <Cell key={r.supplier} fill={r.onTime >= ON_TIME_TARGET ? c.teal : i === 0 ? c.orange : c.blue} />
                  ))}
                  <LabelList dataKey="late" position="right" style={{ fontSize: 10, fill: "#6D6B78" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <LegendDots items={[
            { label: "Late orders", color: c.blue }, { label: "Most late orders", color: c.orange },
            { label: `On time ≥ ${ON_TIME_TARGET}% (favourable)`, color: c.teal },
          ]} />
        </div>
        <div>
          <ChartTitle color={c.navy}>Supplier detail (filtered)</ChartTitle>
          <DataTable
            testid="dash-supply-table"
            columns={[
              { key: "short", label: "Supplier" },
              { key: "orders", label: "Orders", right: true },
              { key: "late", label: "Late", right: true },
              { key: "onTime", label: "On-time", right: true, render: (r) => (
                  <StatusPill color={r.onTime >= ON_TIME_TARGET ? c.teal : c.orange} icon={r.onTime >= ON_TIME_TARGET ? TrendingUp : TrendingDown}>
                    {r.onTime}%
                  </StatusPill>
                ) },
              { key: "sales", label: "Sales", right: true, render: (r) => euro(r.sales) },
              { key: "marginPct", label: "Margin", right: true, render: (r) => `${r.marginPct}%` },
            ]}
            rows={bySupplier}
          />
        </div>
      </div>
      <ChartCaption>Delivery performance against sales and margin, filtered by period, supplier and segment.</ChartCaption>
    </DashFrame>
  );
}
